// ===================================================================
// KAIXO - ChatMessageItem
// ===================================================================
// Renderer del post DENTRO de la vista chat de un canal. Distinto a
// FeedItem porque:
//  - Header muestra el canal (no se repite nombre + avatar por post)
//  - Comentarios: se abren en modal (no inline)
//  - Reaction picker on long-press (emojis extra)
//  - Acciones de owner: Editar + Impulsar
//  - Meta de visibilidad (público / suscriptores)
//  - isDeleted skip del Latido Dorado
// ===================================================================

import React from 'react';
import { Icons } from '../Icons/Icons';
import { linkify } from '../../utils/linkify';
import LatidoCarousel from './LatidoCarousel';
import LatidoDoradoBar from '../LatidoDorado/LatidoDoradoBar';
import { pickId } from './utils';
import ct from './i18n';

const QUICK_REACTIONS = ['❤️', '🔥', '😂', '😮', '😢', '👍'];

export default function ChatMessageItem({
    message,
    channel,
    currentUserId,
    isOwner,
    reactionPickerId,       // id del post con picker abierto (o null)

    onOpenImage,
    onOpenVideo,
    onOpenChannelAvatar,    // () => void
    onReact,                // (messageId, emoji) => void
    onToggleReactionPicker, // (messageId) => void
    onOpenComments,         // (messageId) => void
    onShare,
    onEdit,                 // (editPayload) => void
    onBoost,                // (message) => void
    onChatWithAdmin,        // (ownerId) => void
    onInquiry               // (message, channelId) => void
}) {
    const m = message;
    const uid = String(currentUserId || '');
    const mode = m.effectiveContactMode || channel?.contactMode || 'none';
    const showDirect = !isOwner && (mode === 'direct' || mode === 'both');
    const showForm = !isOwner && (mode === 'form' || mode === 'both');

    // Regla: cada usuario solo puede tener 1 emoji por post. Mostramos
    // contador total y el emoji que el usuario tiene puesto (si lo hay).
    const reactionsObj = m.reactions && typeof m.reactions === 'object' ? m.reactions : {};
    let totalReactions = 0;
    let myEmoji = null;
    for (const [k, users] of Object.entries(reactionsObj)) {
        if (!Array.isArray(users) || users.length === 0) continue;
        totalReactions += users.length;
        if (!myEmoji && users.some(u => String(u) === uid)) {
            myEmoji = k;
        }
    }
    const commentCount = m.commentsCount || 0;
    const sharesCount = m.sharesCount || 0;
    const pickerOpen = reactionPickerId === String(m._id);

    const handleHeartTap = () => {
        if (myEmoji) {
            onReact(String(m._id), myEmoji);
        } else {
            onToggleReactionPicker(String(m._id));
        }
    };

    return (
        <div className="v2-msg">
            {/* Header con info del canal y autor del post */}
            <div className="v2-msg-channel-head">
                <div
                    className="v2-msg-channel-avatar"
                    onClick={onOpenChannelAvatar}
                    style={channel?.avatar ? { cursor: 'pointer' } : {}}
                >
                    {channel?.avatar
                        ? <img src={channel.avatar} alt="" loading="lazy" decoding="async" />
                        : (channel?.name?.[0] || '?').toUpperCase()}
                </div>
                <div className="v2-msg-channel-meta">
                    <div className="v2-msg-channel-title">
                        {channel?.name || '—'}
                        {channel?.handle && (
                            <span className="v2-msg-channel-handle">@{channel.handle}</span>
                        )}
                    </div>
                    <div className="v2-msg-channel-sub">
                        {m.sender?.name || '—'}
                        {' · '}
                        {new Date(m.createdAt).toLocaleString()}
                    </div>
                </div>
            </div>

            {/* Media: carrusel o medio suelto */}
            {(() => {
                if (Array.isArray(m.mediaUrls) && m.mediaUrls.length > 0) {
                    return (
                        <LatidoCarousel
                            media={m.mediaUrls}
                            onOpenImage={onOpenImage}
                            onOpenVideo={onOpenVideo}
                        />
                    );
                }
                if (m.mediaType === 'image' && m.mediaUrl) {
                    return (
                        <img
                            src={m.mediaUrl}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="v2-msg-image"
                            onClick={() => onOpenImage(m.mediaUrl)}
                            style={{ cursor: 'pointer' }}
                        />
                    );
                }
                return null;
            })()}

            {(m.text || m.originalText) && (
                <div className="v2-msg-text">
                    {linkify(m.text || m.originalText)}
                </div>
            )}

            {/* Acciones IG-style: corazón abre picker (1 emoji por user) */}
            <div className="v2-ig-actions">
                <button
                    className={`v2-ig-icon-btn v2-ig-heart ${myEmoji ? 'liked' : ''}`}
                    onClick={handleHeartTap}
                    title={myEmoji ? 'Quitar reacción' : 'Reaccionar'}
                    aria-label="Reaccionar"
                >
                    {myEmoji ? (
                        <span className="v2-ig-heart-emoji">{myEmoji}</span>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                    )}
                </button>
                {totalReactions > 0 && <span className="v2-ig-count">{totalReactions}</span>}

                <button
                    className="v2-ig-icon-btn"
                    onClick={() => onOpenComments(String(m._id))}
                    aria-label="Comentarios"
                    title={m.commentsEnabled ? 'Comentar' : 'Comentarios cerrados'}
                >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                </button>
                {commentCount > 0 && <span className="v2-ig-count">{commentCount}</span>}

                <button
                    className="v2-ig-icon-btn"
                    onClick={() => onShare(m)}
                    aria-label="Compartir"
                >
                    <Icons.Share size={20} />
                </button>
                {sharesCount > 0 && <span className="v2-ig-count">{sharesCount}</span>}
            </div>

            {pickerOpen && (
                <div className="v2-reaction-picker">
                    {QUICK_REACTIONS.map(e => (
                        <button
                            key={e}
                            className="v2-reaction-emoji"
                            onClick={() => {
                                onReact(String(m._id), e);
                                onToggleReactionPicker(null);
                            }}
                        >{e}</button>
                    ))}
                </div>
            )}

            {m.commentsEnabled && commentCount > 0 && (
                <button
                    className="v2-ig-view-comments"
                    onClick={() => onOpenComments(String(m._id))}
                >
                    {ct.viewAllComments ? ct.viewAllComments(commentCount) : `Ver los ${commentCount} comentarios`}
                </button>
            )}

            {/* Latido Dorado (skip si el post fue borrado) */}
            {!m.isDeleted && (
                <LatidoDoradoBar postId={String(m._id)} reactions={m.reactions} />
            )}

            <div className="v2-msg-meta">
                <span className="v2-vis-tag">
                    {m.visibility === 'public'
                        ? <Icons.Globe size={12} />
                        : <Icons.Users size={12} />}
                </span>
                {new Date(m.createdAt).toLocaleString()}
            </div>

            {/* Acciones del owner del canal */}
            <div className="v2-msg-owner-actions">
                {isOwner && !m.isDeleted && (
                    <button
                        className="v2-edit-btn"
                        onClick={() => onEdit({
                            _id: m._id,
                            originalText: m.originalText || m.text || '',
                            visibility: m.visibility || 'subscribers',
                            contactMode: m.contactMode || '',
                            commentsEnabled: !!m.commentsEnabled,
                            interests: Array.isArray(m.interests) ? m.interests : [],
                            isBusiness: channel?.type === 'business'
                        })}
                        title="Editar latido"
                    >
                        <Icons.Edit size={13} /> Editar
                    </button>
                )}
                {isOwner && channel?.type === 'business' && !m.isDeleted && (
                    <button
                        className="v2-boost-btn"
                        onClick={() => onBoost(m)}
                        title="Promocionar este latido"
                    >
                        <Icons.Star size={13} /> Impulsar
                    </button>
                )}
            </div>

            {(showDirect || showForm) && (
                <div className="v2-contact-row">
                    {showDirect && (
                        <button
                            className="v2-contact-btn"
                            onClick={() => onChatWithAdmin(channel?.owner?._id || channel?.owner)}
                        >
                            <Icons.Send size={13} color="white" /> {ct.chatWithAdmin}
                        </button>
                    )}
                    {showForm && (
                        <button
                            className="v2-contact-btn"
                            onClick={() => onInquiry(m, pickId(channel))}
                        >
                            <Icons.Edit size={13} color="white" /> {ct.sendInquiry}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
